import { ProvenanceApiError } from '@/shared/cors-provenance-api';

export const OBFUSCATION_PENDING_MESSAGE = 'This image is being obfuscated';
export const OBFUSCATION_SLOW_MESSAGE =
  'Still obfuscating this image. This is taking longer than expected.';
export const OBFUSCATION_FAILED_MESSAGE = 'The safe preview could not be created.';

export const FLAG_ALREADY_FLAGGED_REASON = 'This file is already flagged.';
export const FLAG_NO_CASE_REASON = 'A case must be selected to report this file.';

/** Job finished with a bound preview — safe to drop the pending overlay. */
export function isObfuscationPreviewReady(input: {
  previewUrl?: string | null;
  jobStatus?: string | null;
}): boolean {
  return Boolean(input.previewUrl?.trim()) && input.jobStatus !== 'IN_PROGRESS';
}

/** Failed blur is terminal even when no preview URL exists. */
export function isObfuscationJobFailed(jobStatus?: string | null): boolean {
  return jobStatus === 'FAILED';
}

export type FlagMenuItemState = {
  id: 'csam';
  label: string;
  disabled: boolean;
  reason?: string;
};

type FlagFileUrlSource = {
  fileInfo?: { fileUrl?: string | null } | null;
  sourceFilePath?: string | null;
  originalUrl?: string | null;
  s3Url?: string | null;
  mediaUrl?: string | null;
  srcToUse?: string | null;
};

/**
 * Canonical file URL for flag POST + blur overlay. MediaFlagMenu and
 * MediaDisplay must resolve the same candidate so IN_PROGRESS tracking
 * matches the identifier used to start obfuscation.
 */
export function resolveFlagFileUrl(item?: FlagFileUrlSource | null): string {
  if (!item) return '';
  return (
    item.fileInfo?.fileUrl ||
    item.sourceFilePath ||
    item.originalUrl ||
    item.s3Url ||
    item.mediaUrl ||
    item.srcToUse ||
    ''
  );
}

/** Pathname only — signed / proxied URLs keep ``?`` / ``#`` off the extension test. */
export function mediaPathForExtension(fileUrl?: string | null): string {
  return (fileUrl || '').trim().split(/[?#]/)[0].toLowerCase();
}

/**
 * A cleared finding ignores leftover type and poll URLs. A new flag only
 * invalidates file-info, so that REVIEWED snapshot stays until the refetch —
 * an in-progress blur must win over it or the original stays on screen.
 */
export function clearedFindingStillApplies(input: {
  findingStatus?: string | null;
  obfuscationInProgress?: boolean;
}): boolean {
  return input.findingStatus === 'REVIEWED' && !input.obfuscationInProgress;
}

/**
 * Callers often leave ``contentModerationType`` unset after a user flag
 * (stale answer/source map). File-info type and the blur-poll preview are
 * enough to keep showing the obfuscated URL instead of the original.
 * A ``REVIEWED`` finding is cleared — type and a leftover poll URL are ignored,
 * matching ``cm?.type && cm.findingStatus !== 'REVIEWED'`` elsewhere.
 */
export function resolveEffectiveContentModerationType(input: {
  contentModerationType?: string | null;
  fileModerationType?: string | null;
  findingStatus?: string | null;
  pollPreviewUrl?: string | null;
  obfuscationInProgress?: boolean;
}): string | undefined {
  if (clearedFindingStillApplies(input)) return undefined;
  const fromProp = input.contentModerationType?.trim();
  if (fromProp) return fromProp;
  const fromFile = input.fileModerationType?.trim();
  if (fromFile) return fromFile;
  if (input.pollPreviewUrl?.trim()) return 'CSAM';
  return input.obfuscationInProgress ? 'CSAM' : undefined;
}

/**
 * Preview URL to show as the safe image.
 * A re-flag of a cleared file must ignore the current preview: file-info still
 * has the original until the new obfuscated URL is polled.
 */
export function resolveBoundSafePreview(input: {
  itemPreviewUrl?: string | null;
  pollPreviewUrl?: string | null;
  ignoreStalePreview?: boolean;
}): string {
  if (input.ignoreStalePreview) return input.pollPreviewUrl?.trim() || '';
  return input.itemPreviewUrl?.trim() || input.pollPreviewUrl?.trim() || '';
}

export function shouldShowCsamSafePreview(input: {
  showObfuscatedPreview?: boolean;
  boundSafePreview?: string | null;
  contentModerationType?: string | null;
  fileModerationType?: string | null;
  findingStatus?: string | null;
  pollPreviewUrl?: string | null;
  obfuscationInProgress?: boolean;
}): boolean {
  if (input.showObfuscatedPreview === false) return false;
  if (!input.boundSafePreview?.trim()) return false;
  return Boolean(
    resolveEffectiveContentModerationType({
      contentModerationType: input.contentModerationType,
      fileModerationType: input.fileModerationType,
      findingStatus: input.findingStatus,
      pollPreviewUrl: input.pollPreviewUrl,
      obfuscationInProgress: input.obfuscationInProgress,
    }),
  );
}

export function isImageMedia(mediaType?: string | null, fileUrl?: string | null): boolean {
  const type = (mediaType || '').toLowerCase();
  if (type === 'image' || type.startsWith('image/')) return true;
  if (type === 'video' || type.startsWith('video/') || type === 'audio') return false;
  return /\.(jpe?g|png|gif|webp|bmp|heic|tif?f)$/.test(mediaPathForExtension(fileUrl));
}

/** Hide the flag control once a file is flagged. A cleared finding has no type. */
export function shouldShowFlagControl(contentModerationType?: string | null): boolean {
  return !contentModerationType?.trim();
}

/**
 * Flag menu gating. Non-image media never reaches this menu. The toolbar
 * and MediaFlagMenu hide the control. Confirm / Not CSAM stay on
 * canVerifyFindings, not this menu.
 */
export function resolveFlagMenuItems(input: {
  alreadyFlagged: boolean;
  canSubmit: boolean;
}): FlagMenuItemState[] {
  const item: FlagMenuItemState = { id: 'csam', label: 'Flag as CSAM', disabled: false };
  if (input.alreadyFlagged) {
    return [{ ...item, disabled: true, reason: FLAG_ALREADY_FLAGGED_REASON }];
  }
  if (!input.canSubmit) {
    return [{ ...item, disabled: true, reason: FLAG_NO_CASE_REASON }];
  }
  return [item];
}

/**
 * Provenance returns 409 when a finding already has an obfuscated copy.
 * That response does not write a finding or start a blur job.
 */
export const FLAG_NOT_SAVED_EXISTING_OBFUSCATION =
  'This file already has an obfuscated copy, but the flag was not saved.';

export function isExistingObfuscationConflict(error: unknown): boolean {
  return error instanceof ProvenanceApiError && error.status === 409;
}

export function flagSubmitErrorMessage(error: unknown): string {
  if (isExistingObfuscationConflict(error)) return FLAG_NOT_SAVED_EXISTING_OBFUSCATION;
  if (error instanceof Error && error.message) return error.message;
  return 'Could not flag this file.';
}
